import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  LabsState,
  LabStationsState,
} from "shared/reducers/frontLoaderReducers";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { useAppSelector } from "app/hooks";
import SelectedStationContext from "app/SelectedStationContext";
import StationSelection from "ui/components/StationSelection";

import "./StationSelectionGate.css";

const StationSelectionGate = ({ children }: { children: React.ReactNode }) => {
  const labs = useSelector(({ labs }: { labs: LabsState }) => labs);
  const labStations = useSelector(
    ({ labStations }: { labStations: LabStationsState }) => labStations
  );
  const location = useLocation();
  const navigate = useNavigate();
  const canChangeLabs = useAppSelector((state) =>
    selectFeatureFlag(state, "canChangeLabs")
  );
  const canChangeStations =
    useAppSelector((state) => selectFeatureFlag(state, "canChangeStations")) ||
    canChangeLabs;

  const labCount = Object.values(labs).length;

  ////////////
  // Lab ID //
  ////////////

  // LocalStorage is used to share the selected Lab between all windows and tabs
  const localStorageLabId = parseInt(
    localStorage.getItem("selectedLabId") || ""
  );
  // Default to first Lab's ID if there is only one Lab to choose from
  const defaultLabId = labCount === 1 ? Object.values(labs)[0].id : undefined;

  const [labId, setLabIdState] = useState<undefined | number>(
    localStorageLabId || defaultLabId
  );
  const setLabId = (newLabId: number | undefined) => {
    localStorage.setItem("selectedLabId", `${newLabId}`);
    setStationId(undefined);
    setLabIdState(newLabId);
  };

  ////////////////
  // Station ID //
  ////////////////

  const desiredStationType =
    location.pathname.startsWith("/cases") ||
    location.pathname.startsWith("/station/shipping")
      ? "accessioning"
      : location.pathname.match(/^\/station\/(\w+)\/?/)?.at(1);

  // GET param is used to open a new tab with a specified Station ID.
  const getParamStationId = canChangeStations
    ? parseInt(new URLSearchParams(window.location.search).get("station") || "")
    : NaN;

  // SessionStorage is unique to this tab and can be used to override the selected Station for a single tab
  const sessionStorageStationId = canChangeStations
    ? parseInt(sessionStorage.getItem("selectedStationId") || "")
    : NaN;

  // LocalStorage is used to share the selected Station between all windows and tabs
  const localStorageStationId = parseInt(
    localStorage.getItem("selectedStationId") || ""
  );

  const [stationId, setStationIdState] = useState<undefined | number>(() => {
    const id =
      getParamStationId ||
      sessionStorageStationId ||
      localStorageStationId ||
      undefined;

    // Allow automatic Station ID selection based on URL (backwards compatability)
    const labStation = labStations[id || 0];
    if (
      (canChangeLabs || canChangeStations) &&
      labId &&
      desiredStationType &&
      desiredStationType !== labStation?.type
    ) {
      const desiredStation = Object.values(labStations).find(
        (ls) => ls.lab_id === labId && ls.type === desiredStationType
      );
      return desiredStation?.id || -1;
    }

    return id;
  });
  const setStationId = (
    newStationId: number | undefined,
    persistToLocalStorage = true
  ) => {
    if (persistToLocalStorage)
      localStorage.setItem("selectedStationId", `${newStationId}`);
    sessionStorage.setItem("selectedStationId", `${newStationId}`);
    setStationIdState(newStationId);
  };

  // Remove Station ID from GET params and persist the Station selection to sessionStorage (for this tab only)
  useEffect(() => {
    if (getParamStationId && getParamStationId !== sessionStorageStationId) {
      console.log("Overriding selected Station");
      sessionStorage.setItem("selectedStationId", `${getParamStationId}`);
      // Remove ?station=XXX from URL
      const url = new URL(window.location.href);
      url.searchParams.delete("station");
      window.history.replaceState(null, "", url);
    }
  }, [getParamStationId, sessionStorageStationId]);

  // Show warning toast after automatically changing Station ID based on URL
  useEffect(() => {
    if (
      stationId &&
      ![
        getParamStationId,
        sessionStorageStationId,
        localStorageStationId,
      ].includes(stationId)
    ) {
      if (stationId === -1) {
        console.error("Unable to automatically change Station.");
      } else {
        const str =
          "Selected Station automatically. It is recommended that you use the Change Station modal instead of navigating between Station URLs.";
        console.warn(str);
      }
    }
  }, []);

  const unset = () => {
    setLabId(defaultLabId);
    setStationId(undefined);
  };

  const lab = labs[labId || 0];
  const labStation = labStations[stationId || 0];

  useEffect(() => {
    // Don't redirect from print pages
    if (
      location.pathname.startsWith("/print/") ||
      location.pathname.startsWith("/batch_print/") ||
      location.pathname.startsWith("/badge/") ||
      location.pathname.startsWith("/print_object/") ||
      location.pathname.startsWith("/slide/")
    )
      return;

    if (
      labStation?.type &&
      labStation?.type !== "accessioning" &&
      !location.pathname.startsWith(`/station/${labStation?.type}/`)
    ) {
      navigate(`/station/${labStation?.type}/`);
    }
    if (
      labStation?.type === "accessioning" &&
      location.pathname.startsWith("/station/") &&
      !(
        location.pathname.startsWith("/station/accessioning") ||
        location.pathname.startsWith("/station/shipping")
      )
    ) {
      navigate("/cases/");
    }
  }, [location.pathname, labStation?.type]);

  useEffect(() => {
    Sentry.configureScope((scope) => {
      scope.setExtra("labId", labId);
    });
  }, [labId]);

  const contextValue = {
    labId,
    setLabId,
    stationId,
    setStationId,
    unset,
  };

  if (!labId || !stationId) return <StationSelection {...contextValue} />;

  if (!lab)
    return (
      <div>
        You do not have access to the selected Lab for this device. Contact an
        administrator for assistance.
      </div>
    );

  if (stationId === -1)
    return (
      <div>
        You tried to change your station by navigating to{" "}
        <code>/station/{desiredStationType}/</code>
        but no {desiredStationType} station could be found for{" "}
        {lab?.name || `LabStation #${stationId}`}.
        <br />
        It is recommended that you use the Change Station modal instead. Click{" "}
        <a href="/">here</a> to return to your configured Station and then click
        the button in the lower right corner of the screen to open the Change
        Station modal.
      </div>
    );

  if (!labStation)
    return (
      <div>
        The selected Lab Station for this device cannot be found. Contact an
        administrator for assistance.
      </div>
    );

  if (labStation.lab_id !== lab.id) {
    setStationId(undefined);
    return null;
  }

  return (
    <SelectedStationContext.Provider value={contextValue}>
      {children}
    </SelectedStationContext.Provider>
  );
};

export default StationSelectionGate;
